import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, MessageCircle, Phone } from "lucide-react";
import { SITE } from "@/constants/site";
import { scrollToSection, useLenis } from "@/components/LenisProvider";
import { EASE } from "@/components/Reveal";

const HERO_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_contact-connect-59/artifacts/df18259efdd3fbc0_IMG_1830.jpeg";

const LINES = [
  { text: "El corazón de" },
  { text: "tu hogar," },
  { text: "a medida.", italic: true },
];

const CHIPS = [
  "Cotización sin costo",
  "Proyecto llave en mano",
  "Visita el taller y pregunta sin compromiso",
];

export default function Hero() {
  const lenis = useLenis();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section
      id="inicio"
      ref={ref}
      className="grain relative overflow-hidden pb-20 pt-32 lg:pb-28 lg:pt-44"
      data-testid="hero-section"
    >
      <span
        aria-hidden="true"
        className="text-stroke-olive pointer-events-none absolute -bottom-10 left-0 hidden select-none font-serif text-[22vw] leading-none lg:block"
      >
        Toscana
      </span>

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-7">
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            data-testid="hero-eyebrow"
          >
            Cocinas Integrales · Hechas a Medida
          </motion.p>

          <h1 className="mt-6 font-serif text-5xl leading-[0.98] tracking-tight text-olive sm:text-7xl lg:text-8xl">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.italic ? "italic text-terracotta" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.05, delay: 0.3 + i * 0.14, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-olive-mid sm:text-lg"
            data-testid="hero-subtitle"
          >
            Desde mi taller en Uruapan, Michoacán, diseño, fabrico e instalo
            cocinas integrales: cualquier tipo de cocina, a tu medida y
            hecha con cariño.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={SITE.phoneHref}
              data-testid="hero-phone-button"
              className="btn-primary group"
            >
              <Phone className="h-5 w-5" />
              Llámanos ahora
            </a>
            <a
              href={SITE.whatsappHref}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-button"
              className="btn-outline group"
            >
              <MessageCircle className="h-4 w-4" />
              O escríbenos por WhatsApp
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
          >
            {CHIPS.map((chip) => (
              <li
                key={chip}
                className="flex items-center gap-2 text-sm text-olive-mid"
              >
                <Check className="h-4 w-4 text-terracotta" />
                {chip}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            className="relative mx-auto max-w-md lg:max-w-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            data-testid="hero-image-frame"
          >
            <motion.div
              className="aspect-[4/5] overflow-hidden rounded-b-[32px] rounded-t-[999px]"
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.3, delay: 0.4, ease: EASE }}
            >
              <motion.img
                src={HERO_IMG}
                alt="Cocina integral terminada con isla de mármol, instalada por Toscana"
                style={{ y: imgY }}
                className="h-full w-full scale-[1.18] object-cover"
                data-testid="hero-image"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
              className="absolute -left-4 bottom-8 rounded-2xl border border-cream-border bg-cream/80 px-6 py-4 shadow-[0_18px_40px_rgba(44,59,41,0.14)] backdrop-blur-xl sm:-left-8"
              data-testid="hero-floating-badge"
            >
              <p className="font-serif text-lg text-olive">
                Diseño · Fabricación · Instalación
              </p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-olive-mid">
                Todo en un solo taller
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
